import { Box, Flex, IconButton, Spinner, VStack } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { FiSend } from "react-icons/fi";
import ReactQuill from "react-quill";
import styled from "styled-components";
import {
	FILE_UPLOAD_TYPES,
	FileUpload,
	UploadedFile,
} from "newui/components/FileUpload";
import Tooltip from "components/Tooltip";
import { createMessage } from "../api";
import { Message } from "../types/Message.type";
import { LengthOfHtmlTextString } from "helpers/stringHelper";
import { showToast } from "utils/helpers";
import { disableReactQuillClasses } from "newui/utils/general";
import { useFileUploadContext } from "newui/components/FileUpload/hooks/FileUploadContext";
import { usePreventAllNavigationDuringUpload } from "newui/components/FileUpload/hooks/usePreventNavigationDuringUpload";
import { FileUploadRef } from "newui/components/FileUpload/types/FileUpload.types";

const QuillWrapper = styled.div`
	.ql-editor {
		border: solid 1px #e5e7eb; /* Tailwind gray-200 */
		border-bottom-left-radius: 0.75rem;
		border-bottom-right-radius: 0.75rem;
		width: 100%;
	}

	width: 100%;
`;

interface MessageComposerProps {
	conversationId: number;
	disabled?: boolean;
	hideSendButton?: boolean;
	onBodyChanged?: (body: string) => void;
	onFilesChanged?: (files: UploadedFile[] | null) => void;
	onNewMessage?: (message: Message) => void;
	onSendingMessage?: (isSending: boolean) => void;
}

/**
 * Allows a user to create a new message
 * @param param0
 * @returns
 */
export const MessageComposer: React.FC<MessageComposerProps> = ({
	conversationId,
	disabled,
	onBodyChanged,
	onFilesChanged,
	onNewMessage,
	onSendingMessage,
	hideSendButton = false,
}) => {
	const [body, setBody] = useState("");
	const [canSubmit, setCanSubmit] = useState(false);
	const [files, setFiles] = useState<UploadedFile[]>([]);
	const { isUploading } = useFileUploadContext();
	const [isSending, setIsSending] = useState(false);
	const uploadRef = useRef<FileUploadRef>(null);

	const { hasUserCancelled } = usePreventAllNavigationDuringUpload();

	const inputRef = useRef(null);

	useEffect(() => {
		setBody("");
		setFiles(null);
	}, [conversationId, hasUserCancelled]);

	// remove auto-focus on toolbar buttons in quill, go to entry first
	useEffect(() => {
		const toolbar = document.querySelector(".ql-toolbar");
		if (!toolbar) return;

		const items = toolbar.querySelectorAll("button, select");
		items.forEach((el) => el.setAttribute("tabindex", "-1"));
	}, []);

	const sendMessage = () => {
		setIsSending(true);
		onSendingMessage(true);
		createMessage(conversationId, body, files)
			.then((result) => {
				if (result) {
					onNewMessage(result);
					setBody("");
					setFiles(null);
					uploadRef.current?.clear();
				}
			})
			.catch((error) => {
				showToast(
					`There was an error sending your message: ${error.message}`,
					"Error"
				);
			})
			.finally(() => {
				setIsSending(false);
				onSendingMessage(false);
			});
	};

	const isValidKey = (e) => {
		// Allow control/meta keys (copy/paste/select all)
		if (e.ctrlKey || e.metaKey) {
			return true;
		}
		return true;
	};

	const onBodyChange = (value: string) => {
		setBody(value);
		setCanSubmit(LengthOfHtmlTextString(value) > 0);
		onBodyChanged && onBodyChanged(value);
	};

	const onFilesUploadedChanged = (uploadedFiles: UploadedFile[] | null) => {
		setFiles(uploadedFiles);
		onFilesChanged?.(uploadedFiles);
	};

	const disableInput = isSending || disabled;

	return (
		<Box p={3}>
			<Flex
				gap={2}
				align="center"
				direction={"row"}
			>
				<QuillWrapper>
					<ReactQuill
						ref={inputRef}
						style={{ minHeight: "10px" }}
						placeholder={"Type your message..."}
						readOnly={disableInput}
						modules={{
							toolbar: [
								["bold", "italic", "underline", "strike", "blockquote"], // Standard formatting
								[{ list: "ordered" }, { list: "bullet" }], // Lists
								["link"], // Links and images
							],
						}}
						className={`textarea w-full [&_.ql-blank::before]:!text-gray-400 test_${name} ${disableInput && disableReactQuillClasses(true)} ${disableInput && "custom-quill"}`}
						value={body}
						onChange={onBodyChange}
						onKeyPress={isValidKey}
						onKeyDown={isValidKey}
					/>
				</QuillWrapper>
				<VStack gap={2}>
					{/* <Tooltip label="Attach file(s)">
						<IconButton
							aria-label="attach"
							icon={<FiPaperclip size={30} />}
						/>
					</Tooltip> */}
					{!hideSendButton && !isSending && (
						<Tooltip
							label={`Send message${isUploading ? " (wait for uploads to finish)" : ""}${!canSubmit ? " (please type a message)" : ""}`}
						>
							<IconButton
								aria-label="send message"
								icon={<FiSend size={30} />}
								onClick={canSubmit && sendMessage}
								disabled={!canSubmit || isUploading || disableInput}
							/>
						</Tooltip>
					)}
					{isSending && (
						<>
							<span className="text-xs">Sending...</span>
							<Spinner size={"xs"} />
						</>
					)}
				</VStack>
			</Flex>
			<FileUpload
				ref={uploadRef}
				description="You can attach one more files to your message"
				extraData={{ conversationId }}
				multiple
				onChange={onFilesUploadedChanged}
				supportsHardDelete={false}
				uploadType={FILE_UPLOAD_TYPES.CONVERSATION_RFX_PRE_SUBMISSION}
				disabled={disableInput}
			/>
		</Box>
	);
};
